import { useEffect, useId, useRef, useState, type CSSProperties } from 'react';
import {
  ArrowRight,
  Check,
  ChevronDown,
  Clock,
  Code2,
  FileCheck2,
  Layers,
  ShieldCheck,
  SlidersHorizontal,
  Users,
  Wallet,
  X,
  type LucideIcon,
} from 'lucide-react';

import { Container } from '@/components/ui/container';
import { Eyebrow, Section } from '@/components/ui/section';
import { EthIcon, UsdcIcon } from '@/components/ui/token-icons';
import { cn } from '@/lib/utils';
import './policy-page.css';

type CapabilityKey =
  | 'approvals'
  | 'scope'
  | 'assets'
  | 'limits'
  | 'contracts'
  | 'automation'
  | 'changes';
type Capability = {
  key: CapabilityKey;
  label: string;
  summary: string;
  title: string;
  body: string;
  bullets: readonly string[];
  Icon: LucideIcon;
};

const CAPABILITIES: readonly Capability[] = [
  {
    key: 'approvals',
    label: 'Approval thresholds',
    summary: 'The right people, the right threshold',
    Icon: Users,
    title: 'Choose who approves. Set how many.',
    body: 'Require approvals from a specific person or a group, with the threshold that fits the operation. A vendor-payment policy can require 2 of 5 members, while another policy governs routine transfers with its own approval requirements.',
    bullets: [
      'Set an N-of-M threshold for a group.',
      'Give different operations different approval rules.',
      'Use automatic approval for flows you have explicitly permitted.',
    ],
  },
  {
    key: 'scope',
    label: 'People & accounts',
    summary: 'Clear responsibilities across your wallets',
    Icon: Wallet,
    title: 'Give each team its own scope.',
    body: 'Define which accounts a policy covers and which people or groups can initiate transactions. Your Operations team can prepare a payment while the Administrator group provides the required approvals. Organize treasury, operating, and customer accounts around their own workflows.',
    bullets: [
      'Scope a policy to selected source accounts.',
      'Choose an initiating member or group.',
      'Separate requesting a transaction from approving it.',
    ],
  },
  {
    key: 'assets',
    label: 'Tokens & recipients',
    summary: 'Control what moves and where it goes',
    Icon: Layers,
    title: 'Keep transfers inside clear boundaries.',
    body: 'Use token and destination conditions to describe the transfers a policy permits. Limit a vendor-payment flow to USDC and approved recipients, or give another account a different set of permissions. The selected policy determines which requests qualify.',
    bullets: [
      'Choose an allowed token or broader token scope.',
      'Maintain approved destination addresses.',
      'Apply different policies to different payment flows.',
    ],
  },
  {
    key: 'limits',
    label: 'Spending limits',
    summary: 'Bound individual transfers and ongoing use',
    Icon: SlidersHorizontal,
    title: 'Set limits that fit your operations.',
    body: 'Set a token-transfer amount limit for each transfer or across a configured time interval. Track usage per initiator, source account, or destination, or combine usage across them. Choose the interval and its start time to define when the spending allowance resets.',
    bullets: [
      'Choose a token and set its transfer limit.',
      'Cap total transfers over a configured interval.',
      'Choose the start time and scope for the allowance.',
    ],
  },
  {
    key: 'contracts',
    label: 'Contract permissions',
    summary: 'Permit a specific action, down to its inputs',
    Icon: Code2,
    title: 'Allow the action you actually intend.',
    body: 'A contract permission can be more precise than access to an entire application. Scope a policy to a contract address, permitted functions, and argument conditions. For example, allow a supply action in a lending contract while keeping a withdrawal outside that policy.',
    bullets: [
      'Choose the destination contract.',
      'Allow specific functions.',
      'Constrain argument values for the permitted call.',
    ],
  },
  {
    key: 'automation',
    label: 'Bounded automation',
    summary: 'Move routine work into your application',
    Icon: Code2,
    title: 'Automate within the rules you set.',
    body: 'Use the API and TypeScript SDK to initiate transactions under your organization’s policies. An automatic-approval policy can handle a permitted routine flow without manual member approvals, while a policy for another operation can require a group to sign.',
    bullets: [
      'Use a programmatic API member as an initiator.',
      'Apply transaction conditions to automated requests.',
      'Keep manual approval for operations that need it.',
    ],
  },
  {
    key: 'changes',
    label: 'Policy changes',
    summary: 'Keep control of the controls',
    Icon: FileCheck2,
    title: 'Review the rules before changing them.',
    body: 'Policy changes follow a proposal workflow. Review the intended changes and collect the required administrative approvals before they take effect. The authority to change a policy is distinct from the members allowed to approve transactions under it.',
    bullets: [
      'Review proposed additions and edits.',
      'Apply your organization’s administrative approval requirements.',
      'Keep policy administration separate from day-to-day payment approval.',
    ],
  },
];

const CAPABILITY_DURATION_MS = 12_000;
const CAPABILITY_TICK_MS = 100;
const MIN_VISIBLE_FRACTION = 0.2;
const EXAMPLE_APPROVERS = ['Alex', 'Sam', 'Jo', 'Lee', 'Rae'];

/** Own the tour clock; stop it while hidden, offscreen, hovered, or keyboard-focused. */
function useCapabilityTour() {
  const ref = useRef<HTMLDivElement>(null);
  const [playback, setPlayback] = useState({ index: 0, elapsedMs: 0 });
  const [onScreen, setOnScreen] = useState(false);
  const [pageVisible, setPageVisible] = useState(true);
  const [reducedMotion, setReducedMotion] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [keyboardFocused, setKeyboardFocused] = useState(false);

  useEffect(() => {
    const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const updateMotion = () => setReducedMotion(motion.matches);
    const updateVisibility = () => setPageVisible(!document.hidden);
    updateMotion();
    updateVisibility();
    motion.addEventListener('change', updateMotion);
    document.addEventListener('visibilitychange', updateVisibility);
    return () => {
      motion.removeEventListener('change', updateMotion);
      document.removeEventListener('visibilitychange', updateVisibility);
    };
  }, []);

  useEffect(() => {
    if (!ref.current) return;
    if (!('IntersectionObserver' in window)) {
      setOnScreen(true);
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) =>
        setOnScreen(
          !!entry?.isIntersecting &&
            entry.intersectionRatio >= MIN_VISIBLE_FRACTION
        ),
      { threshold: [0, MIN_VISIBLE_FRACTION] }
    );
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const running =
    onScreen && pageVisible && !reducedMotion && !hovered && !keyboardFocused;
  useEffect(() => {
    if (!running) return;
    const timer = window.setInterval(() => {
      // Functional updates preserve a manual selection made between clock ticks.
      setPlayback((current) =>
        current.elapsedMs + CAPABILITY_TICK_MS >= CAPABILITY_DURATION_MS
          ? { index: (current.index + 1) % CAPABILITIES.length, elapsedMs: 0 }
          : { ...current, elapsedMs: current.elapsedMs + CAPABILITY_TICK_MS }
      );
    }, CAPABILITY_TICK_MS);
    return () => window.clearInterval(timer);
  }, [running]);

  return {
    ref,
    index: playback.index,
    progress: (playback.elapsedMs / CAPABILITY_DURATION_MS) * 100,
    running,
    setHovered,
    setKeyboardFocused,
    select: (index: number) => setPlayback({ index, elapsedMs: 0 }),
  };
}

function VisualRow({
  title,
  detail,
  allowed = true,
}: {
  title: string;
  detail: string;
  allowed?: boolean;
}) {
  return (
    <div className="policy-visual-row">
      <div>
        <strong>{title}</strong>
        <span>{detail}</span>
      </div>
      <span className={cn('policy-visual-result', !allowed && 'is-denied')}>
        {allowed ? <Check /> : <X />}
        {allowed ? 'Allowed' : 'Outside policy'}
      </span>
    </div>
  );
}

function CapabilityVisual({ capability }: { capability: CapabilityKey }) {
  switch (capability) {
    case 'approvals':
      return (
        <>
          <div className="policy-visual-rule">
            <span>Member approval threshold</span>
            <strong>
              2 <span>of</span> 5
            </strong>
          </div>
          <div className="policy-visual-members">
            {EXAMPLE_APPROVERS.map((member, index) => (
              <div key={member}>
                <span className={cn(index < 2 && 'is-signed')}>
                  {member[0]}
                  {index < 2 && <Check />}
                </span>
                <strong>{member}</strong>
                <small>{index < 2 ? 'Signed' : 'Pending'}</small>
              </div>
            ))}
          </div>
          <div className="policy-visual-success">
            <ShieldCheck /> Required member approvals collected
          </div>
          <p className="policy-visual-caption">
            One group. A threshold that fits the payment.
          </p>
        </>
      );
    case 'scope':
      return (
        <>
          <div className="policy-visual-heading">
            <Wallet />
            <div>
              <span>Policy scope</span>
              <strong>Operations → Administrator group</strong>
            </div>
          </div>
          <VisualRow
            title="Treasury account"
            detail="Operations can initiate"
          />
          <VisualRow
            title="Operating account"
            detail="Operations can initiate"
          />
          <VisualRow
            title="Reserve account"
            detail="Outside this policy’s scope"
            allowed={false}
          />
          <div className="policy-visual-success">
            <Users /> Administrator group provides the required approvals
          </div>
        </>
      );
    case 'assets':
      return (
        <>
          <div className="policy-visual-tokens">
            <span>
              <UsdcIcon width={30} height={30} /> USDC <Check />
            </span>
            <span className="is-muted">
              <EthIcon width={30} height={30} /> ETH <X />
            </span>
          </div>
          <VisualRow title="Design partner" detail="Approved vendor" />
          <VisualRow title="Operations supplier" detail="Approved vendor" />
          <VisualRow
            title="Unknown destination"
            detail="Not in this policy’s recipient list"
            allowed={false}
          />
          <p className="policy-visual-caption">
            Allowed asset and destination conditions work together.
          </p>
        </>
      );
    case 'limits':
      return (
        <>
          <div className="policy-visual-rule">
            <span>USDC budget per 24 hours</span>
            <strong>
              5,000 <small>USDC</small>
            </strong>
          </div>
          <div className="policy-budget">
            <span>
              <strong>1,250 USDC used</strong>
              <span>25%</span>
            </span>
            <div>
              <i />
            </div>
            <span>
              <span>Resets every 24 hours</span>
              <strong>3,750 USDC remaining</strong>
            </span>
          </div>
          <VisualRow
            title="Spending scope"
            detail="All transfers from this account combined"
          />
          <div className="policy-visual-success">
            <Clock /> Defined amounts, periods, and usage scopes
          </div>
        </>
      );
    case 'contracts':
      return (
        <>
          <div className="policy-visual-heading">
            <span className="policy-aave-mark">A</span>
            <div>
              <span>Permitted contract</span>
              <strong>Lending pool</strong>
            </div>
          </div>
          <VisualRow
            title="supply(asset, amount)"
            detail="Permitted function and asset arguments"
          />
          <VisualRow
            title="withdraw()"
            detail="Outside the permitted function list"
            allowed={false}
          />
          <div className="policy-argument">
            <code>asset</code>
            <span>
              <UsdcIcon width={17} height={17} /> USDC
            </span>
          </div>
          <div className="policy-argument">
            <code>amount</code>
            <span>≤ 5,000 USDC</span>
          </div>
          <p className="policy-visual-caption">
            Contract address + function + argument conditions.
          </p>
        </>
      );
    case 'automation':
      return (
        <>
          <div className="policy-visual-heading">
            <Code2 />
            <div>
              <span>Authorized API member</span>
              <strong>Payment service</strong>
            </div>
          </div>
          <div className="policy-api-example">
            <span>Policy</span>
            <code>Routine vendor payments</code>
            <span>Member approvals</span>
            <strong>Automatic approval</strong>
          </div>
          {['250', '500', '1,000'].map((amount) => (
            <div className="policy-automated-row" key={amount}>
              <span>{amount} USDC</span>
              <span>
                <Check /> Within policy
              </span>
            </div>
          ))}
          <p className="policy-visual-caption">
            Routine requests stay inside the configured permissions.
          </p>
        </>
      );
    case 'changes':
      return (
        <>
          <div className="policy-visual-heading">
            <FileCheck2 />
            <div>
              <span>Policy change proposal</span>
              <strong>Update the vendor-payment limit</strong>
            </div>
          </div>
          <div className="policy-change-values">
            <span>
              <small>Current limit</small>5,000 USDC/day
            </span>
            <ArrowRight />
            <span>
              <small>Proposed limit</small>7,500 USDC/day
            </span>
          </div>
          <ol className="policy-change-stages">
            {[
              'Review changes',
              'Collect admin approvals',
              'Activate approved policy',
            ].map((stage) => (
              <li key={stage}>
                <Check />
                {stage}
              </li>
            ))}
          </ol>
          <div className="policy-visual-success">
            <ShieldCheck /> Policy change approved
          </div>
        </>
      );
  }
}

export function PolicyCapabilities() {
  const tour = useCapabilityTour();
  const panelId = useId();
  return (
    <Section className="policy-capabilities bg-white py-10 sm:py-12 lg:py-14">
      <Container>
        <div className="policy-capabilities-heading">
          <Eyebrow>Explore the controls</Eyebrow>
          <h2>
            Build policies around
            <br />
            <span>the way your team works.</span>
          </h2>
          <p>
            Bring approvals, permissions, and spending boundaries into the same
            workflow. Explore each capability, then try the controls for
            yourself.
          </p>
        </div>
        <div
          ref={tour.ref}
          className="policy-explorer"
          data-running={tour.running}
          style={{ '--policy-progress': `${tour.progress}%` } as CSSProperties}
          onPointerDownCapture={() => tour.setKeyboardFocused(false)}
          onFocusCapture={(event) =>
            tour.setKeyboardFocused(event.target.matches(':focus-visible'))
          }
          onKeyDownCapture={() => tour.setKeyboardFocused(true)}
          onBlurCapture={(event) => {
            if (!event.currentTarget.contains(event.relatedTarget))
              tour.setKeyboardFocused(false);
          }}
        >
          <nav className="policy-cap-nav" aria-label="Policy capabilities">
            <ol>
              {CAPABILITIES.map(({ key, label, summary, Icon }, index) => (
                <li key={key}>
                  <button
                    type="button"
                    className={cn(index === tour.index && 'is-active')}
                    aria-pressed={index === tour.index}
                    aria-controls={`${panelId}-${key}`}
                    onClick={() => tour.select(index)}
                  >
                    <Icon aria-hidden="true" />
                    <span>
                      <strong>{label}</strong>
                      <small>{summary}</small>
                    </span>
                    <span className="policy-cap-progress" aria-hidden="true" />
                  </button>
                </li>
              ))}
            </ol>
          </nav>
          <div
            className="policy-cap-content"
            onPointerEnter={(event) => {
              if (event.pointerType === 'mouse') tour.setHovered(true);
            }}
            onPointerLeave={() => tour.setHovered(false)}
          >
            {CAPABILITIES.map((capability, index) => (
              <article
                key={capability.key}
                id={`${panelId}-${capability.key}`}
                data-policy-capability={capability.key}
                className={cn(
                  'policy-cap-panel',
                  index === tour.index && 'is-active'
                )}
                aria-labelledby={`${panelId}-${capability.key}-title`}
              >
                <div className="policy-cap-description">
                  <h3 id={`${panelId}-${capability.key}-title`}>
                    {capability.title}
                  </h3>
                  <p>{capability.body}</p>
                  <ul>
                    {capability.bullets.map((bullet) => (
                      <li key={bullet}>
                        <Check aria-hidden="true" />
                        {bullet}
                      </li>
                    ))}
                  </ul>
                  <a href="#policy-playground">
                    Try a policy <ArrowRight aria-hidden="true" />
                  </a>
                </div>
                <div className="policy-cap-visual" aria-hidden="true">
                  <div className="policy-visual-bar">
                    <img src="/den-logo.svg" alt="" />
                    <span>Policy configuration</span>
                  </div>
                  <div className="policy-visual-body">
                    <CapabilityVisual capability={capability.key} />
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </Container>
    </Section>
  );
}

/** Keep the full FAQ answer aligned with the explanations in the animated tour. */
export function PolicyCapabilitiesFaq() {
  const answerId = useId();
  return (
    <details
      name="page-faq"
      className="group border-t border-hairline"
      data-policy-capabilities-faq="true"
    >
      <summary className="flex cursor-pointer list-none items-center justify-between gap-4 py-5 text-left [&::-webkit-details-marker]:hidden">
        <h3 className="text-base font-medium text-ink-900 sm:text-lg">
          Which policy controls can I configure?
        </h3>
        <ChevronDown
          aria-hidden="true"
          className="size-5 shrink-0 text-ink-400 transition-transform duration-300 group-open:rotate-180"
        />
      </summary>
      <div className="max-w-3xl space-y-6 pb-6 pr-4 sm:pr-9">
        {CAPABILITIES.map((capability) => (
          <section
            key={capability.key}
            data-policy-capability={capability.key}
            aria-labelledby={`${answerId}-${capability.key}`}
          >
            <h4
              id={`${answerId}-${capability.key}`}
              className="mb-2 text-sm font-medium text-ink-900 sm:text-base"
            >
              {capability.label}: {capability.title}
            </h4>
            <p className="text-sm leading-relaxed text-ink-500">
              {capability.body}
            </p>
            <ul className="mt-3 list-disc space-y-1 pl-5 text-sm leading-relaxed text-ink-500">
              {capability.bullets.map((bullet) => (
                <li key={bullet}>{bullet}</li>
              ))}
            </ul>
          </section>
        ))}
      </div>
    </details>
  );
}
